<template>
  <div class="item-spinner">
    <v-img
      v-show="game.status === GAME_STATUS.rolling"
      :src="require('@/assets/img/games/wheel/arrow.png')"
      contain width="72" aspect-ratio="1"
      class="arrow arrow-left"
    />
    <v-img
      v-show="game.status === GAME_STATUS.rolling"
      :src="require('@/assets/img/games/wheel/arrow.png')"
      contain width="72" aspect-ratio="1"
      class="arrow arrow-right"
    />

    <v-container fluid fill-height class="item-spinner-inner">
      <v-row class="full-height">
        <v-col v-for="team in game.teams" :key="team.id" :cols="12 * (team.slots / game.config.slots)" class="py-0">
          <div class="team d-flex justify-center full-width full-height">
            <div
              v-for="slot in team.slots" :key="slot"
              class="flex-grow-1 player-slot text-center"
              :class="`slot-player-${slotPlayerId(team.id, slot)}`"
            >
              <div class="slot-line" />
              <canvas ref="confetti" class="confetti-cannon" />
              <div class="spinner-items">
                <v-responsive
                  v-for="(spinnerItem, i) in slotSpinner(team.id, slot)"
                  :key="i"
                  :width="itemSize"
                  :aspect-ratio="1"
                  :class="{'is-won-item': spinnerItem.isWon}"
                >
                  <CaseSpinnerItem :item="spinnerItem" no-bg class="fill-height" />
                </v-responsive>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import confetti from 'canvas-confetti'
import anime from 'animejs/lib/anime.es'
import seededRandom from 'seed-random'

import CaseSpinnerItem from '@/components/CaseSpinnerItem'
import { registerSound } from '@/modules/sound'
import { getRandomIntInRange } from '@/utils'
import { pickItems } from '../../../Cases/Case/utils'
import { GAME_STATUS } from '../../utils'

const sounds = {
  land: registerSound('games/cases/land', { volume: 0.3 }),
  bigWin: registerSound('games/cases/big_win', { volume: 0.7 }),
  ticks: [
    registerSound('games/wheel/tick1', { volume: 0.15 }),
    registerSound('games/wheel/tick2', { volume: 0.15 }),
    registerSound('games/wheel/tick3', { volume: 0.15 }),
    registerSound('games/wheel/tick4', { volume: 0.15 }),
  ],
  cannon: [
    registerSound('games/case-battles/jackpot1', { volume: 0.2 }),
    registerSound('games/case-battles/jackpot2', { volume: 0.2 }),
    registerSound('games/case-battles/jackpot3', { volume: 0.2 }),
  ],
}

export default {
  components: {
    CaseSpinnerItem,
  },
  props: {
    game: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    itemdb: {
      type: Object,
      required: true,
    },
    itemCount: {
      type: Number,
      default: 20,
    },
    itemCountBuffer: {
      type: Number,
      default: 3,
    },
    itemSize: {
      type: Number,
      default: 144,
    },
    muted: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    animation: null,
    spinners: {},
  }),
  computed: {
    crate() {
      const { round } = this.game
      const { rounds, cases } = this.game.config

      return cases[rounds[round - 1]]
    },
    crateWeight() {
      return this.crate.items.reduce((bcc, v) => bcc + v.weight, 0)
    },
    items() {
      const { itemdb, crateWeight } = this

      return this.crate?.items.map(item => ({
        ...itemdb[item.id],
        ...item,
        odds: item.weight / crateWeight,
      })) ?? []
    },
    hasWeight() {
      return this.crateWeight > 0
    },
    GAME_STATUS() {
      return GAME_STATUS
    },
  },
  beforeDestroy() {
    this.killAnim()
  },
  methods: {
    async spin(wonItems, timeSinceRoll) {
      this.killAnim()

      const seed = this.game.id
      this.spinners = this.generateSpinners(wonItems, seed)

      // wait for render
      await this.$nextTick()

      const { round } = this.game
      const {
        itemSize, itemCount, muted, crate,
      } = this
      const totalSize = itemCount * itemSize
      const halfItemSize = itemSize / 2

      const rollOffsets = []
      for (let i = 0; i < this.game.config.slots; i++) {
        const rand = seededRandom(`${seed}-${round}-${i}`)()
        const monkaSFactor = rand * (-halfItemSize - halfItemSize) + halfItemSize

        const rollOffset = totalSize + halfItemSize + monkaSFactor
        const easingModifier = +(rand * (0.70 - 0.95) + 0.95).toFixed(2)

        rollOffsets.push({
          offset: -rollOffset,
          rollback: -(totalSize + halfItemSize),
          easing: easingModifier,
        })
      }

      let lastOffset = 0

      this.animation = anime
        .timeline({ autoplay: false })
        .add({
          targets: '.arrow.arrow-left',
          left: [{ value: -16 }, { value: 0 }],
          easing: 'easeInOutCirc',
          duration: 400,
        })
        .add({
          targets: '.arrow.arrow-right',
          right: [{ value: -16 }, { value: 0 }],
          easing: 'easeInOutCirc',
          duration: 400,
        }, 0)
        .add({
          targets: '.item-spinner .spinner-items',
          translateY(el, i, l) {
            return rollOffsets[i].offset
          },
          translateX: ['-50%', '-50%'],
          top: ['50%', '50%'],
          duration: this.config.time.roll,
          easing(el, i, l) {
            const { easing } = rollOffsets[i]
            return `cubicBezier(0.5, 1, ${easing}, 1)`
          },
          update(anim) {
            // use cached
            if (muted) return

            // only looking at one of the spinners otherwise earrape
            const val = parseFloat(anim.animations?.[0]?.currentValue ?? 0)
            const offset = Math.abs(val)

            if (offset > lastOffset + itemSize) {
              sounds.ticks[getRandomIntInRange(0, 3)].play()
              lastOffset = offset
            }
          },
        }, 0)
        .add({
          targets: '.item-spinner .spinner-items',
          translateY: -(totalSize + halfItemSize),
          translateX: ['-50%', '-50%'],
          duration: 500,
          easing: 'easeInOutCirc',
          begin: anim => {
            if (this.muted) return
            sounds.land.play()
          },
          complete: () => {
            const bigWins = Object.values(this.spinners)
              .map(i => i.find(o => o.isWon && o.odds <= 0.05 && o.price > crate.price))
              .filter(Boolean)

            if (bigWins.length) {
              const playerIds = bigWins.map(i => i.playerId)

              const $cannons = this.$el.querySelectorAll(
                playerIds.map(i => `.slot-player-${i} canvas.confetti-cannon`).join(','),
              )

              for (const $cannon of $cannons) {
                $cannon.confetti = $cannon.confetti || confetti.create($cannon, { resize: true })
                $cannon.confetti({ spread: 50, origin: { y: 1.5 } })
              }

              if (!this.muted) {
                sounds.cannon[getRandomIntInRange(0, 2)].play()
              }
            }
          },
        }, '+=300')
        .add({
          targets: '.is-won-item .spinner-item .image',
          scale: [1, 1.2],
          duration: 2e3,
          easing: 'easeOutQuart',
        })
        .add({
          targets: '.is-won-item .spinner-item .price',
          opacity: [0, 1],
          duration: 500,
          easing: 'easeOutQuart',
        }, '-=1500')

      // don't seek for no reason
      if (timeSinceRoll > 250) {
        this.animation.seek(Math.min(this.animation.duration, timeSinceRoll))
      }

      if (timeSinceRoll < this.animation.duration) {
        this.animation.play()
      }
    },
    killAnim() {
      if (!this.animation) return

      this.animation.pause()
      this.animation.seek(0)
      this.animation.animatables.forEach(({ target }) => {
        anime.remove(target)
      })
    },
    slotSpinner(teamId, slot) {
      return this.spinners[this.game.playersByTeam(teamId)?.[slot - 1]?.steamid] ?? []
    },
    slotPlayerId(teamId, slot) {
      return this.game.playersByTeam(teamId)?.[slot - 1]?.steamid ?? null
    },
    //
    generateSpinners(wonItems, seed) {
      const { round, players } = this.game
      const { items } = this

      const spinners = {}
      for (const steamid of Object.keys(players)) {
        spinners[steamid] = this.generateSpinnerItems(items, wonItems[steamid], `${seed}-${round}-${steamid}`)
      }

      return spinners
    },
    generateSpinnerItems(items, item, seed) {
      if (!items?.length) return []
      if (!seed) return []

      const weightRemappedItems = items.map(i => ({
        ...i,
        weight: Math.ceil((i.weight ** 0.4) * 100),
      }))

      const { itemCount, itemCountBuffer, hasWeight } = this
      const spinnerItems = Array(itemCount + itemCountBuffer)

      for (let i = 0; i < spinnerItems.length; i++) {
        if (i === itemCount && item) {
          const itemInfo = items.find(o => o.id === item.id)
          spinnerItems[i] = { ...itemInfo, ...item, isWon: true }
        } else {
          const rand = seededRandom(`${seed}-${i}`)()
          let randItem
          if (hasWeight) {
            randItem = pickItems(weightRemappedItems, rand)
          } else {
            randItem = items[Math.floor(rand * items.length)]
          }

          spinnerItems[i] = randItem
        }
      }

      return spinnerItems
    },
  },
}
</script>

<style lang="scss" scoped>
.item-spinner {
  position: relative;
  height: 300px;

  .item-spinner-inner {
    overflow: hidden;
    position: relative;
    height: 100%;
  }
}

.arrow {
  position: absolute;
  z-index: 4;
  top: 50%;
  pointer-events: none;

  &-left {
    left: 0;
    transform: translate(-30%, -50%) rotate(-90deg);
  }

  &-right {
    right: 0;
    transform: translate(30%, -50%) rotate(90deg);
  }
}

.team {
  gap: 16px;
}

.player-slot {
  position: relative;
  z-index: 2;
  max-width: 275px;

  .confetti-cannon {
    @include overlay;

    z-index: 5;
    pointer-events: none;
  }

  .slot-line {
    position: absolute;
    z-index: 0;
    top: 50%;
    width: 100%;
    height: 1px;
    background: linear-gradient(to right, transparent, primary(500), transparent);
    transform: translateY(-50%);
    pointer-events: none;
  }

  .spinner-items {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    will-change: transform;
  }
}

</style>
